/* eslint-disable react/no-unescaped-entities */
import { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Scissors, Loader2, Undo2, ZoomIn, ZoomOut, Move } from "lucide-react";
import { renderImageUrl } from "@/lib/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Split-component dialog (Option A) — freehand LASSO on the source
 * image + component name → matte-white isolated render (or same-frame
 * subtraction when action="remove").
 *
 * NEW in v2:
 *   • Action toggle: Isolate (default) vs Remove.
 *   • Zoom + pan on the lasso surface (wheel, +/- buttons, drag-to-pan
 *     when zoomed) — designers can now lasso small side figures on
 *     large temple pieces precisely.
 *   • Backend receives {component_name, action, bbox?}. Bbox is only
 *     sent when action="isolate" AND lasso was drawn (remove keeps the
 *     full frame — see routers/studio.py::studio_split_component).
 */
export default function SplitComponentDialog({ open, onOpenChange, target, onSubmit, cost = 1 }) {
  const [componentName, setComponentName] = useState("");
  const [action, setAction] = useState("isolate"); // "isolate" | "remove"
  const [submitting, setSubmitting] = useState(false);
  const [polygon, setPolygon] = useState([]); // normalized 0..1 points
  const [drawing, setDrawing] = useState(false);

  // Zoom + pan state — transform applied to both the image AND the
  // lasso overlay so they stay perfectly aligned.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const reset = useCallback(() => {
    setComponentName("");
    setAction("isolate");
    setPolygon([]);
    setSubmitting(false);
    setDrawing(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setPanning(false);
  }, []);

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange?.(v);
  };

  // Paint the polygon overlay whenever it changes.
  // Canvas is drawn in image-normalized space (0..1) so the polygon
  // stays anchored to the image even when zoom/pan changes — the CSS
  // transform on the canvas element itself handles the visual mapping.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const { clientWidth: w, clientHeight: h } = container;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, w, h);
    if (polygon.length < 2) return;

    ctx.beginPath();
    polygon.forEach((p, i) => {
      const x = p.x * w, y = p.y * h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    if (!drawing) ctx.closePath();
    ctx.fillStyle = "rgba(212, 175, 55, 0.16)";
    ctx.fill();
    ctx.lineWidth = 1.5 / zoom;
    ctx.strokeStyle = "rgba(212, 175, 55, 0.9)";
    ctx.stroke();

    ctx.fillStyle = "rgba(212, 175, 55, 1)";
    polygon.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x * w, p.y * h, 2 / zoom, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [polygon, drawing, zoom]);

  // Convert client pointer → image-normalized [0..1] coordinate
  // accounting for current zoom + pan.
  const pointFromEvent = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    if (cx == null || cy == null) return null;
    // Screen-space [0..1] within container
    const sx = (cx - rect.left) / rect.width;
    const sy = (cy - rect.top) / rect.height;
    // Undo transform: (image_norm - 0.5) * zoom + 0.5 + pan_norm = screen_norm
    // ⇒ image_norm = ((screen_norm - pan_norm - 0.5) / zoom) + 0.5
    const panNormX = pan.x / rect.width;
    const panNormY = pan.y / rect.height;
    const ix = (sx - panNormX - 0.5) / zoom + 0.5;
    const iy = (sy - panNormY - 0.5) / zoom + 0.5;
    return { x: Math.max(0, Math.min(1, ix)), y: Math.max(0, Math.min(1, iy)) };
  }, [zoom, pan]);

  // ----- Pointer handlers: pan takes priority when spacebar / middle
  // button held; otherwise draw lasso. -----
  const onPointerDown = (e) => {
    if (e.button === 1 || e.shiftKey) {
      e.preventDefault();
      setPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
      return;
    }
    e.preventDefault();
    const p = pointFromEvent(e);
    if (!p) return;
    setDrawing(true);
    setPolygon([p]);
  };

  const onPointerMove = (e) => {
    if (panning) {
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setPan({ x: panStart.current.panX + dx, y: panStart.current.panY + dy });
      return;
    }
    if (!drawing) return;
    const p = pointFromEvent(e);
    if (!p) return;
    setPolygon((prev) => {
      if (prev.length === 0) return [p];
      const last = prev[prev.length - 1];
      const dx = (p.x - last.x), dy = (p.y - last.y);
      if (dx * dx + dy * dy < 0.0002) return prev;
      return [...prev, p];
    });
  };

  const onPointerUp = () => {
    setDrawing(false);
    setPanning(false);
  };

  // Wheel zoom — anchored on cursor position for natural feel.
  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 5) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => Math.max(1, Math.min(6, z * factor)));
  };

  const zoomIn = () => setZoom((z) => Math.min(6, z * 1.4));
  const zoomOut = () => setZoom((z) => {
    const next = Math.max(1, z / 1.4);
    if (next === 1) setPan({ x: 0, y: 0 }); // snap pan when fully zoomed out
    return next;
  });
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const clearLasso = () => setPolygon([]);

  if (!target) return null;

  const hasLasso = polygon.length >= 3;
  // Lasso is now the source of truth. When drawn, name is optional.
  // Without a lasso, we still require a text description (>=2 chars)
  // as a fallback so the backend has *something* to work with.
  const canSubmit = !submitting && (hasLasso || componentName.trim().length >= 2);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let bbox = null;
      let poly = null;
      if (hasLasso) {
        const xs = polygon.map((p) => p.x);
        const ys = polygon.map((p) => p.y);
        bbox = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
        // Send the full polygon so the backend can mask out everything
        // outside the lasso outline — otherwise we send a rectangular
        // bbox that includes irrelevant neighbouring content and the
        // model returns a raw crop instead of an isolated component.
        poly = polygon.map((p) => [
          Math.max(0, Math.min(1, p.x)),
          Math.max(0, Math.min(1, p.y)),
        ]);
      }
      await onSubmit?.(target, componentName.trim(), bbox, action, poly);
      handleOpenChange(false);
    } catch {
      // Parent's `submitSplitComponent` has already toasted the actual
      // reason. Swallow the rejection here so it doesn't bubble into
      // React's tree and trigger the "Uncaught runtime errors" overlay.
      // Dialog stays open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  const imgSrc = target ? renderImageUrl(API, target) : "";
  const transformStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    transformOrigin: "center center",
  };

  const isolateMode = action === "isolate";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border border-[#D4AF37]/22 text-white max-w-2xl p-0 overflow-hidden"
        data-testid="split-component-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle className="font-display text-lg tracking-tight inline-flex items-center gap-2">
            <Scissors className="w-4 h-4 text-[#D4AF37]" /> Split component
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs leading-relaxed">
            Draw a lasso around ONE element, pick a mode and name it.
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="px-5 flex items-center gap-2">
          <div className="inline-flex rounded-full border border-white/10 p-0.5 bg-[#0F0F0F]">
            <button
              type="button"
              onClick={() => setAction("isolate")}
              data-testid="split-mode-isolate"
              className={
                "px-3 py-1.5 rounded-full text-[11px] transition " +
                (isolateMode ? "bg-[#D4AF37] text-black font-medium" : "text-white/60 hover:text-white")
              }
            >
              Isolate on white
            </button>
            <button
              type="button"
              onClick={() => setAction("remove")}
              data-testid="split-mode-remove"
              className={
                "px-3 py-1.5 rounded-full text-[11px] transition " +
                (!isolateMode ? "bg-[#D4AF37] text-black font-medium" : "text-white/60 hover:text-white")
              }
            >
              Remove & keep rest
            </button>
          </div>
          <span className="text-[10px] text-white/40">
            {isolateMode
              ? "Extract lassoed element on white bg"
              : "Subtract lassoed element, keep full frame"}
          </span>
        </div>

        {/* Lasso surface */}
        <div className="px-5 pt-3">
          <div
            ref={containerRef}
            className="relative w-full aspect-square max-h-[52vh] rounded-lg overflow-hidden border border-white/10 bg-black select-none touch-none"
            style={{ cursor: panning ? "grabbing" : (zoom > 1 ? "crosshair" : "crosshair") }}
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={onPointerUp}
            onMouseLeave={onPointerUp}
            onTouchStart={onPointerDown}
            onTouchMove={onPointerMove}
            onTouchEnd={onPointerUp}
            onWheel={onWheel}
            data-testid="split-lasso-surface"
          >
            <img
              src={imgSrc}
              alt="source"
              draggable={false}
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              style={transformStyle}
            />
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={transformStyle}
            />
            {polygon.length < 3 && !drawing && (
              <div className="absolute bottom-2 left-2 right-2 text-center text-[10.5px] text-white/55 bg-black/45 backdrop-blur-sm rounded-md py-1.5 pointer-events-none">
                Drag to lasso · scroll or +/− to zoom · Shift-drag to pan
              </div>
            )}

            {/* Zoom controls — floating top-right */}
            <div className="absolute top-2 right-2 flex flex-col gap-1 pointer-events-auto">
              <button
                type="button"
                onClick={zoomIn}
                data-testid="split-zoom-in"
                title="Zoom in"
                className="w-7 h-7 rounded-md bg-black/70 border border-white/15 text-white/85 hover:bg-black/90 flex items-center justify-center"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={zoomOut}
                data-testid="split-zoom-out"
                disabled={zoom <= 1}
                title="Zoom out"
                className="w-7 h-7 rounded-md bg-black/70 border border-white/15 text-white/85 hover:bg-black/90 disabled:opacity-30 flex items-center justify-center"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              {zoom > 1 && (
                <button
                  type="button"
                  onClick={resetView}
                  data-testid="split-zoom-reset"
                  title="Reset view"
                  className="w-7 h-7 rounded-md bg-black/70 border border-white/15 text-white/85 hover:bg-black/90 flex items-center justify-center text-[9px] font-mono"
                >
                  {zoom.toFixed(1)}×
                </button>
              )}
            </div>
          </div>

          {/* Lasso controls */}
          <div className="mt-2 flex items-center justify-between text-[10.5px]">
            <span className="text-white/40">
              {hasLasso
                ? `Lassoed · ${polygon.length} points`
                : (isolateMode ? "No lasso · whole piece considered" : "No lasso · nothing to remove yet")}
              {zoom > 1 && (
                <span className="ml-2 inline-flex items-center gap-1 text-[#D4AF37]/70">
                  <Move className="w-3 h-3" /> Shift-drag to pan
                </span>
              )}
            </span>
            {hasLasso && (
              <button
                type="button"
                onClick={clearLasso}
                data-testid="split-lasso-clear"
                className="inline-flex items-center gap-1 text-white/50 hover:text-white transition"
              >
                <Undo2 className="w-3 h-3" /> Clear lasso
              </button>
            )}
          </div>
        </div>

        {/* Name + submit */}
        <div className="px-5 pb-5 pt-3 border-t border-white/5 mt-3">
          <label
            htmlFor="split-component-name"
            className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block"
          >
            {hasLasso ? (
              <>{isolateMode ? "Name (optional)" : "Name (optional)"} — <span className="text-[#D4AF37]/70 normal-case tracking-normal">lasso is source of truth</span></>
            ) : (
              isolateMode ? "Which component to isolate?" : "Which element to remove?"
            )}
          </label>
          <input
            id="split-component-name"
            value={componentName}
            onChange={(e) => setComponentName(e.target.value)}
            placeholder={hasLasso ? "Optional — describe what you lassoed (or leave blank)" : (isolateMode ? "e.g. central Shiv Parvati figure" : "e.g. central Shiv Parvati figure")}
            maxLength={120}
            autoFocus
            data-testid="split-component-name-input"
            className="w-full rounded-lg bg-[#0F0F0F] border border-white/8 text-white text-sm px-4 py-3 outline-none focus:border-[#D4AF37]/50 placeholder:text-white/25"
          />
          <div className="mt-2 text-[10.5px] text-white/40 leading-relaxed">
            {hasLasso ? (
              <>The area you lassoed is the source of truth — the model will extract exactly what's inside it. A name helps only if the lasso is ambiguous.</>
            ) : isolateMode ? (
              <>Examples: <em>"central Shiv Parvati figure"</em>, <em>"left side attendant figure"</em>, <em>"peacock motif"</em>. Or draw a lasso above for pixel-precise selection.</>
            ) : (
              <>Examples: <em>"central Shiv Parvati figure"</em>, <em>"ghungroo dangles at the bottom"</em>, <em>"decorative top crown"</em>. Or draw a lasso above.</>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between text-[11px]">
            <span className="text-white/45">
              Cost: <span className="text-[#D4AF37] font-medium">{cost} credit</span>
              {" · "}~5 s
            </span>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="split-component-submit-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-[13px] font-medium px-4 py-2 hover:bg-[#F3CE5A] transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {isolateMode ? "Isolating…" : "Removing…"}</>
              ) : (
                <>{isolateMode ? "Isolate on white →" : "Remove element →"}</>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
